/**
 * OmniClinicalBridge IndexedDB Cache Manager
 * OmniAlera Technologies Holdings Inc.
 * 
 * Provides offline storage for medicines and clinical conditions data
 * Critical for healthcare providers in areas with limited connectivity
 */

const DB_NAME = 'OmniClinicalBridgeDB';
const DB_VERSION = 1;

// Store names
export const STORES = {
  MEDICINES: 'medicines',
  CONDITIONS: 'conditions',
  CALCULATORS: 'calculators',
  FAVORITES: 'favorites',
  RECENT_SEARCHES: 'recentSearches',
  OFFLINE_QUEUE: 'offlineQueue',
  USER_PREFERENCES: 'userPreferences'
} as const;

type StoreName = typeof STORES[keyof typeof STORES];

let dbInstance: IDBDatabase | null = null;

/**
 * Initialize IndexedDB database
 */
export async function initDB(): Promise<IDBDatabase> {
  if (dbInstance) return dbInstance;
  
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => {
      console.error('[IndexedDB] Failed to open database:', request.error);
      reject(request.error);
    };
    
    request.onsuccess = () => {
      dbInstance = request.result;
      console.log('[IndexedDB] Database opened successfully');
      resolve(dbInstance);
    };
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // Medicines store with indexes for quick searching
      if (!db.objectStoreNames.contains(STORES.MEDICINES)) {
        const medicineStore = db.createObjectStore(STORES.MEDICINES, { keyPath: 'id' });
        medicineStore.createIndex('genericName', 'genericName', { unique: false });
        medicineStore.createIndex('therapeuticClass', 'therapeuticClass', { unique: false });
        medicineStore.createIndex('atcCode', 'atcCode', { unique: false });
      }
      
      // Clinical conditions store
      if (!db.objectStoreNames.contains(STORES.CONDITIONS)) {
        const conditionStore = db.createObjectStore(STORES.CONDITIONS, { keyPath: 'id' });
        conditionStore.createIndex('category', 'category', { unique: false });
        conditionStore.createIndex('severity', 'severity', { unique: false });
        conditionStore.createIndex('isEmergency', 'isEmergency', { unique: false });
      }
      
      // Clinical calculators
      if (!db.objectStoreNames.contains(STORES.CALCULATORS)) {
        const calcStore = db.createObjectStore(STORES.CALCULATORS, { keyPath: 'id' });
        calcStore.createIndex('category', 'category', { unique: false });
      }
      
      // User favorites
      if (!db.objectStoreNames.contains(STORES.FAVORITES)) {
        const favStore = db.createObjectStore(STORES.FAVORITES, { keyPath: 'id' });
        favStore.createIndex('type', 'type', { unique: false });
        favStore.createIndex('addedAt', 'addedAt', { unique: false });
      }
      
      // Recent searches for quick access
      if (!db.objectStoreNames.contains(STORES.RECENT_SEARCHES)) {
        const searchStore = db.createObjectStore(STORES.RECENT_SEARCHES, { keyPath: 'id', autoIncrement: true });
        searchStore.createIndex('timestamp', 'timestamp', { unique: false });
        searchStore.createIndex('query', 'query', { unique: false });
      }
      
      // Offline queue for sync when online
      if (!db.objectStoreNames.contains(STORES.OFFLINE_QUEUE)) {
        const queueStore = db.createObjectStore(STORES.OFFLINE_QUEUE, { keyPath: 'id', autoIncrement: true });
        queueStore.createIndex('timestamp', 'timestamp', { unique: false });
        queueStore.createIndex('type', 'type', { unique: false });
      }
      
      // User preferences
      if (!db.objectStoreNames.contains(STORES.USER_PREFERENCES)) {
        db.createObjectStore(STORES.USER_PREFERENCES, { keyPath: 'key' });
      }
      
      console.log('[IndexedDB] Database schema created/upgraded');
    };
  });
}

/**
 * Get all items from a store
 */
export async function getAll<T>(storeName: StoreName): Promise<T[]> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get single item by key
 */
export async function get<T>(storeName: StoreName, key: string | number): Promise<T | undefined> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(key);
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Put (add or update) item
 */
export async function put<T>(storeName: StoreName, item: T): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(item);
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Put multiple items (bulk insert/update)
 */
export async function putMany<T>(storeName: StoreName, items: T[]): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    
    items.forEach(item => store.put(item));
    
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

/**
 * Delete item by key
 */
export async function remove(storeName: StoreName, key: string | number): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(key);
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all items in a store
 */
export async function clear(storeName: StoreName): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Search by index
 */
export async function getByIndex<T>(
  storeName: StoreName,
  indexName: string,
  value: IDBValidKey
): Promise<T[]> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const index = store.index(indexName);
    const request = index.getAll(value);
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Count items in store
 */
export async function count(storeName: StoreName): Promise<number> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.count();
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Check if database has data (for initial sync check)
 */
export async function hasData(storeName: StoreName): Promise<boolean> {
  const itemCount = await count(storeName);
  return itemCount > 0;
}

/**
 * Get database storage estimate
 */
export async function getStorageEstimate(): Promise<{ usage: number; quota: number } | null> {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    const estimate = await navigator.storage.estimate();
    return {
      usage: estimate.usage || 0,
      quota: estimate.quota || 0
    };
  }
  return null;
}

/**
 * Close database connection
 */
export function closeDB(): void {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}
